<template>
  <div class="inventory-identity">
    <!-- Line 1: listing dot, condition, Year Make Model link, trim, draft, tags -->
    <div class="inventory-identity__line1">
      <v-tooltip location="top">
        <template #activator="{ props: tooltipProps }">
          <span
            v-bind="tooltipProps"
            class="status-dot flex-none"
            :class="listingIndicator"
            role="img"
            :aria-label="listingIndicatorLabel"
          />
        </template>
        <span>{{ listingIndicatorLabel }}</span>
      </v-tooltip>

      <v-chip v-if="conditionLabel" size="x-small" variant="tonal" class="flex-none">
        {{ conditionLabel }}
      </v-chip>

      <v-tooltip location="top" :text="title">
        <template #activator="{ props: tooltipProps }">
          <router-link
            v-bind="tooltipProps"
            :to="{ name: 'VehicleDetails', params: { vin: vehicle.vin } }"
            class="inventory-identity__title text-truncate"
            data-testid="inventory-vehicle-link"
            @click.stop
          >
            {{ title }}
          </router-link>
        </template>
      </v-tooltip>

      <v-tooltip v-if="vehicle.trim" location="top">
        <template #activator="{ props: tooltipProps }">
          <span
            v-bind="tooltipProps"
            class="inventory-identity__trim text-medium-emphasis text-truncate"
            tabindex="0"
          >
            {{ vehicle.trim }}
          </span>
        </template>
        <span>Trim: {{ vehicle.trim }}</span>
      </v-tooltip>

      <v-chip
        v-if="vehicle.publishState === 'draft'"
        size="x-small"
        variant="tonal"
        color="warning"
        class="flex-none"
      >
        Draft
      </v-chip>

      <v-menu v-if="tagCount > 0" open-on-hover open-on-click location="bottom">
        <template #activator="{ props: menuProps }">
          <button
            v-bind="menuProps"
            type="button"
            class="inventory-identity__tags flex-none"
            :aria-label="`Show ${tagCount} tags`"
            data-testid="inventory-tags-trigger"
            @click.stop
          >
            <v-icon icon="mdi-tag-outline" size="14" />
            <span>+{{ tagCount }}</span>
          </button>
        </template>

        <v-card class="pa-3" max-width="320">
          <VehicleTagChips :tags="vehicle.tags" :system-tags="vehicle.systemTags" />
        </v-card>
      </v-menu>
    </div>

    <!-- Line 2: stock number and VIN (never wraps) -->
    <div class="inventory-identity__line2 text-caption text-medium-emphasis">
      <span>
        Stock #
        <v-tooltip
          v-if="vehicle.stock_number && vehicle.stock_number.length > 7"
          location="top"
          :text="vehicle.stock_number"
        >
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps" style="white-space: nowrap" tabindex="0">{{ vehicle.stock_number.slice(0, 7) }}…</span>
          </template>
        </v-tooltip>
        <span v-else>{{ vehicle.stock_number ?? '—' }}</span>
      </span>
      <span class="inventory-identity__vin">{{ vehicle.vin }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import VehicleTagChips from '@/components/VehicleTagChips.vue'
import type { InventoryVehicle } from '@/services/inventory'
import type { ListingIndicator } from '@/utils/inventoryListingIndicator'

// Presentational two-line identity cell. No emits, no store or service access. The Year Make Model title is the
// Vehicle Details link, styled as plain title text; it stops click propagation so the row handler does not run,
// and RouterLink's own navigation handles the single navigation.
const props = defineProps<{
  vehicle: InventoryVehicle
  listingIndicator: ListingIndicator
  listingIndicatorLabel: string
  conditionLabel: string | null
}>()

const tagCount = computed(
  () => (props.vehicle.tags?.length ?? 0) + (props.vehicle.systemTags?.length ?? 0),
)

const title = computed(() => {
  const parts = [props.vehicle.year, props.vehicle.make, props.vehicle.model]
    .map((part) => (part == null ? '' : String(part).trim()))
    .filter((part) => part !== '')
  return parts.length > 0 ? parts.join(' ') : '—'
})
</script>

<style scoped>
.inventory-identity {
  min-width: 0;
}

/* Line 1 is a width-capped flex row: width 0 + min-width 100% removes it from the cell's min-/max-content
   so the column floor is line 2; the padding/negative margin keep focus outlines inside the clip box. */
.inventory-identity__line1 {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
  width: 0;
  min-width: 100%;
  overflow: hidden;
  padding-block: 3px;
  margin-block: -3px;
}

.inventory-identity__line2 {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
  margin-top: 2px;
  white-space: nowrap;
}

.flex-none {
  flex: none;
}

/* The ancestor-qualified selector outranks the global `a:not(.v-list-item):not(.v-btn)` link colour and
   hover background in assets/main.css, so the title reads as plain text. */
.inventory-identity a.inventory-identity__title {
  flex: 0 1 auto;
  min-width: 0;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-weight: 500;
  text-decoration: none;
  border-radius: 2px;
}

.inventory-identity a.inventory-identity__title:hover {
  background-color: transparent;
}

.inventory-identity a.inventory-identity__title:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 1px;
}

/* The trim gives up its width before the Year Make Model link: the very large shrink factor keeps
   the link's proportional share below one layout unit until the trim reaches its 2.5em stub. */
.inventory-identity__trim {
  flex: 0 100000 auto;
  min-width: 2.5em;
  max-width: 140px;
  display: inline-block;
  font-size: 0.8125rem;
}

.inventory-identity__tags {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 6px;
  border-radius: 9999px;
  font-size: 0.75rem;
  line-height: 1.25rem;
  color: rgb(var(--v-theme-on-surface));
  background-color: rgba(var(--v-theme-on-surface), 0.08);
  cursor: pointer;
}

.inventory-identity__tags:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 1px;
}

.inventory-identity__vin {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  display: inline-block;
  opacity: 0.9;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
}

.status-dot.listed {
  background-color: rgb(var(--v-theme-success));
}

.status-dot.hidden {
  background-color: rgb(var(--v-theme-warning));
}

.status-dot.archived {
  background-color: rgb(var(--v-theme-error));
}

.status-dot.draft {
  background-color: rgb(var(--v-theme-warning));
}

.status-dot.unknown {
  background-color: rgba(0, 0, 0, 0.35);
}
</style>
